import { getLocalStorageObject, setLocalStorageObject, useMountEffect } from '@timelyapp/react';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { ThunkDispatch } from 'types/redux';
import { setQuery } from '../redux/modules/forecast.actions';
import { getForecastQuery } from '../redux/modules/forecast.selectors';
import { forecastQuerySchema } from '../utils/forecastQuerySchema';

const STORAGE_KEY = 'timely-tasks_query';

/**
 * Persists the forecast query (task filters) to localStorage so that the last
 * selected filters are restored when the user returns to the Tasks page.
 *
 * URL query parameters take priority — if the URL already contains filter params
 * (e.g. a shared link), those will override the stored values via
 * useQueryParameterStateSync which runs after this hook.
 *
 * This hook is intentionally called from both ForecastPage and
 * ForecastAnalyticsRoute. This is safe because both are separate routes and
 * are never mounted simultaneously, so there is no risk of double-dispatch.
 */
export function useForecastQueryStorage() {
  const dispatch = useDispatch<ThunkDispatch>();
  const query = useSelector(getForecastQuery);
  const isMounted = useRef(false);

  useMountEffect(() => {
    const result = forecastQuerySchema.safeParse(getLocalStorageObject<unknown>(STORAGE_KEY));
    if (result.success) {
      dispatch(setQuery(result.data));
    }
  });

  useEffect(() => {
    // Skip the initial effect run to avoid writing the default query to
    // localStorage before the stored query has been dispatched on mount.
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    setLocalStorageObject(STORAGE_KEY, query);
  }, [query]);
}
